"use client";

import * as React from "react";
import { Slot, Slottable } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../../utils/cn";
import { RxChevronRight } from "@react-icons/all-files/rx/RxChevronRight";

const buttonVariants = cva(
    "focus-visible:ring-border-primary inline-flex gap-3 items-center justify-center whitespace-nowrap ring-offset-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
    {
        variants: {
            variant: {
                primary: "btn-primary border",
                secondary: "btn-secondary border border-border-primary",
                "secondary-alt":
                    "btn-secondary border border-border-alternative bg-transparent text-text-alternative backdrop-blur-[10px]",
                tertiary: "text-text-primary",
                link: "border-0 gap-2 hover:underline",
                "link-alt": "border-0 text-text-alternative gap-2",
                ghost: "hover:bg-background-alternative hover:text-text-alternative",
            },
            size: {
                primary: "px-6 py-3",
                sm: "px-5 py-2",
                link: "p-0",
                icon: "size-10",
            },
        },
        defaultVariants: {
            variant: "primary",
            size: "primary",
        },
    },
);

type CustomProps = {
    iconLeft?: React.ReactNode;
    iconRight?: React.ReactNode;
    asChild?: boolean;
};

export interface ButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    CustomProps,
    VariantProps<typeof buttonVariants> { }

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    (
        {
            className,
            variant,
            size,
            iconLeft,
            iconRight,
            children,
            asChild = false,
            ...props
        },
        ref,
    ) => {
        const Comp = asChild ? Slot : "button";
        size = ["link", "link-alt"].includes(variant ?? "") ? "link" : size;
        return (
            <Comp
                className={cn(buttonVariants({ variant, size, className }))}
                ref={ref}
                {...props}
            >
                {iconLeft && iconLeft}
                <Slottable>{children}</Slottable>
                {iconRight ? iconRight : (["link", "link-alt"].includes(variant ?? "") && (
                    <RxChevronRight />
                ))}
            </Comp>
        );
    },
);
Button.displayName = "Button";

export { Button, buttonVariants };
